import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MasterService } from 'app/service/master.service';
import { FinishProductService } from 'app/service/finish-product.service';
import { FssaiGeneratedTagService } from 'app/service/fssai-generated-tag.service';
import { environment } from 'environments/environment';
import { from, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { extractMetadataOptions } from '../assigner-bulk-upload/assigner-bulk-upload.util';
import { STANDARD_MACRO_LABELS } from 'app/shared/metadata/micro-nutrition.util';
import {
  FP_BULK_NET_QTY_UNITS,
  FP_BULK_PRODUCT_FORMS,
  FP_BULK_PRODUCT_TYPES,
  FP_BULK_SHELF_LIFE_UNITS,
  FP_BULK_STATUS_OPTIONS,
  FpBulkClaimDraft,
  FpBulkEntitySelections,
  FpBulkHealthConditionGroup,
  FpBulkLookups,
  FpBulkLookupOption,
  FpBulkNutrientItem,
  FpBulkPreviewRow,
  FpBulkResolvedIngredient,
  FpBulkServingMeasurement,
  FpBulkSkuDraft,
} from './super-admin-finish-product-bulk-upload.models';
import { getFpBulkFieldErrors, rebuildFpBulkPayload } from './super-admin-finish-product-bulk-upload.util';
import {
  applyTaxonomyToIngredient,
  enrichFpBulkRowsFromIngredients,
  extractFpIngredientTaxonomyFromDetail,
  FpBulkIngredientTaxonomy,
  syncFpBulkAllergensFromIngredients,
} from './super-admin-finish-product-bulk-ingredient-enricher';

interface FpPincodeLookupItem {
  id: string;
  pincodeId: string;
  pincode: string;
  label: string;
  name: string;
  area?: string;
  cityId: string;
  cityName: string;
  stateId: string;
  stateName: string;
  countryId: string;
  countryName: string;
  isLookup?: boolean;
}

@Component({
  selector: 'app-super-admin-finish-product-bulk-upload-row-editor',
  templateUrl: './super-admin-finish-product-bulk-upload-row-editor.component.html',
  styleUrls: ['./super-admin-finish-product-bulk-upload-row-editor.component.scss'],
})
export class SuperAdminFinishProductBulkUploadRowEditorComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() row!: FpBulkPreviewRow;
  @Input() lookups!: FpBulkLookups;

  activeTab = 1;
  fieldErrors: Record<string, string> = {};
  sectionErrorMessages: string[] = [];

  readonly editorTabs: Array<{ id: number; short: string; label: string }> = [
    { id: 1, short: 'Company', label: '1. Company Details' },
    { id: 2, short: 'Product', label: '2. Product Details' },
    { id: 3, short: 'Entities', label: '3. Manufactured / Marketed / Packaged' },
    { id: 4, short: 'Import/Export', label: '4. Imported / Exported' },
    { id: 5, short: 'SKU', label: '5. SKU Details' },
    { id: 6, short: 'Cat. / Sensory', label: '6. Categories / Sensory / Geo' },
    { id: 7, short: 'Allergens', label: '7. Allergen & Intolerance + Observed' },
    { id: 8, short: 'UOM', label: '8. Unit of Measurements' },
    { id: 9, short: 'Ingredients', label: '9. Ingredients' },
    { id: 10, short: 'Nutrients', label: '10. Nutrient Information' },
    { id: 11, short: 'Claims & tags', label: '11. Health Tags / Claims / Benefits' },
    { id: 12, short: 'Ing. claims', label: '12. Ingredient Claims' },
    { id: 13, short: 'Rework', label: '13. Rework Suggested' },
    { id: 14, short: 'Directions+', label: '14. Directions / Warnings / Disclaimer / Certification' },
    { id: 15, short: 'Status', label: '15. Status' },
  ];

  get activeTabLabel(): string {
    return this.editorTabs.find((t) => t.id === this.activeTab)?.label || '';
  }

  readonly section3EntityDefs = [
    { key: 'manufacturedBy' as const, label: 'Manufactured by', nameKey: 'manufacturedByName', addressKey: 'manufacturedByAddress', licenseKey: 'manufacturedByLicenseNo' },
    { key: 'marketedBy' as const, label: 'Marketed by', nameKey: 'marketedByName', addressKey: 'marketedByAddress', licenseKey: 'marketedByLicenseNo' },
    { key: 'packagedBy' as const, label: 'Packaged by', nameKey: 'packagedByName', addressKey: 'packagedByAddress', licenseKey: 'packagedByLicenseNo' },
  ];

  readonly section4EntityDefs = [
    { key: 'importedBy' as const, label: 'Imported by', nameKey: 'importedByName', addressKey: 'importedByAddress', licenseKey: 'importedByLicenseNo' },
    { key: 'exportedBy' as const, label: 'Exported by', nameKey: 'exportedByName', addressKey: 'exportedByAddress', licenseKey: 'exportedByLicenseNo' },
    { key: 'packagedBySection4' as const, label: 'Packaged by', nameKey: 'packagedBySection4Name', addressKey: 'packagedBySection4Address', licenseKey: 'packagedBySection4LicenseNo' },
  ];

  readonly statusOptions = FP_BULK_STATUS_OPTIONS.map((s) => ({ value: s, label: s }));
  readonly productForms = FP_BULK_PRODUCT_FORMS.map((v) => ({ value: v, label: v }));
  readonly shelfLifeUnits = FP_BULK_SHELF_LIFE_UNITS;
  readonly netQtyUnits = FP_BULK_NET_QTY_UNITS;

  get productTypes(): Array<{ value: string; label: string }> {
    const names =
      this.lookups?.productTypes?.length > 0 ? this.lookups.productTypes : FP_BULK_PRODUCT_TYPES;
    return (names || []).map((v) => ({ value: v, label: v }));
  }
  readonly ratingOptions = [
    { value: 1, label: 'Poor' },
    { value: 2, label: 'Fair' },
    { value: 3, label: 'Good' },
    { value: 4, label: 'Very Good' },
    { value: 5, label: 'Excellent' },
  ];

  companyStates: FpBulkLookupOption[] = [];
  companyCities: FpBulkLookupOption[] = [];
  /** City-filtered OR pincode-search results (with hierarchy). */
  companyPincodeItems: FpPincodeLookupItem[] = [];
  loadingCompanyStates = false;
  loadingCompanyCities = false;
  loadingCompanyPincodes = false;
  searchingCompanyPincode = false;

  companyPincodeTypeahead = new Subject<string>();
  private companyPincodeTypeaheadSub?: Subscription;
  private pincodeLookupCache = new Map<string, FpPincodeLookupItem>();
  private applyingPincode = false;

  entityStateOptions: Record<string, FpBulkLookupOption[]> = {};
  entityCityOptions: Record<string, FpBulkLookupOption[]> = {};
  entityPincodeItems: Record<string, FpPincodeLookupItem[]> = {};
  entityPincodeTypeahead: Record<string, Subject<string>> = {};
  entitySearchingPincode: Record<string, boolean> = {};
  private entityPincodeSubs: Subscription[] = [];

  readonly entityKeys: Array<{
    key: keyof Pick<
      FpBulkPreviewRow['selections'],
      | 'manufacturedBy'
      | 'marketedBy'
      | 'packagedBy'
      | 'importedBy'
      | 'exportedBy'
      | 'packagedBySection4'
    >;
    label: string;
  }> = [
    { key: 'manufacturedBy', label: 'Manufactured by' },
    { key: 'marketedBy', label: 'Marketed by' },
    { key: 'packagedBy', label: 'Packaged by' },
    { key: 'importedBy', label: 'Imported by' },
    { key: 'exportedBy', label: 'Exported by' },
    { key: 'packagedBySection4', label: 'Packaged by (Sec 4)' },
  ];

  @ViewChild('ingredientDialogTpl') ingredientDialogTpl!: TemplateRef<unknown>;
  @ViewChild('healthGroupModalTpl') healthGroupModalTpl!: TemplateRef<unknown>;
  @ViewChild('uomDialogTpl') uomDialogTpl!: TemplateRef<unknown>;
  @ViewChildren(NgSelectComponent) pageNgSelects!: QueryList<NgSelectComponent>;
  private scrollCloseCleanupFns: Array<() => void> = [];
  ingredientEditIndex: number | null = null;
  ingredientSearchResults: Array<{ id: number; name: string }> = [];
  selectedIngredientId: number | null = null;
  selectedIngredientName = '';
  ingredientServingUnits: Array<{
    servingUnitMasterId: number | string;
    displayLabel: string;
    valueInGram?: number;
  }> = [];
  selectedServingUnitId: number | string | null = null;
  ingredientAmount: number | null = null;
  ingredientSearchLoading = false;
  pendingUnitSearchTerm = '';
  /** Taxonomy loaded when an ingredient is picked from master (applied on save). */
  private pendingIngredientTaxonomy: FpBulkIngredientTaxonomy | null = null;
  ingredientTypeahead = new Subject<string>();
  private ingredientTypeaheadSub?: Subscription;
  private ingredientModalRef: NgbModalRef | null = null;
  private healthModalRef: NgbModalRef | null = null;
  private uomModalRef: NgbModalRef | null = null;

  readonly standardMacroLabels = [...STANDARD_MACRO_LABELS];
  standardMacroOptions: Array<{ value: string; label: string }> = this.standardMacroLabels.map((l) => ({
    value: l,
    label: l,
  }));
  selectedMacroToAdd: string | null = null;
  loadingFssaiTags = false;
  private fssaiTagTimer: ReturnType<typeof setTimeout> | null = null;

  healthGroupSearch = '';
  healthModalSearch = '';
  healthModalSelection = new Set<string>();
  private healthExpanded = new Set<string>();
  entityLicenseUploading: Record<string, boolean> = {};

  productUomCategories: Array<{ id: number; name: string; selected: boolean; adding: boolean }> = [];
  productUomLoading = false;
  selectedUomFilter: string | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private masterService: MasterService,
    private finishProductService: FinishProductService,
    private fssaiTagService: FssaiGeneratedTagService,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  get parsed() {
    return this.row.parsed;
  }

  get selections() {
    return this.row.selections;
  }

  async ngOnInit(): Promise<void> {
    this.initCompanyPincodeTypeahead();
    this.initEntityPincodeTypeaheads();
    this.initIngredientTypeahead();
    this.ensureExtendedSelectionDefaults();
    this.normalizeMultiSelectIds();
    this.hydrateClaimAvailableTags();

    await this.hydrateCompanyLocationFromCsvHints();
    for (const e of this.entityKeys) {
      await this.hydrateEntityLocationFromCsvHints(e.key);
    }
    await this.ensureIngredientsEnriched();
    this.scheduleLucideIconRender();
  }

  ngAfterViewInit(): void {
    this.scheduleLucideIconRender();
    this.attachScrollCloseListeners();
  }

  ngOnDestroy(): void {
    this.detachScrollCloseListeners();
    this.companyPincodeTypeaheadSub?.unsubscribe();
    this.ingredientTypeaheadSub?.unsubscribe();
    this.entityPincodeSubs.forEach((s) => s.unsubscribe());
    this.companyPincodeTypeahead.complete();
    this.ingredientTypeahead.complete();
    Object.values(this.entityPincodeTypeahead).forEach((s) => s.complete());
    try {
      this.ingredientModalRef?.dismiss();
    } catch {
      /* noop */
    }
    try {
      this.uomModalRef?.dismiss();
    } catch {
      /* noop */
    }
    try {
      this.healthModalRef?.dismiss();
    } catch {
      /* noop */
    }
  }

  /** Template hook — body scroll closes open dropdowns. */
  onEditorBodyScroll(event: Event): void {
    this.handlePageScrollClose(event);
  }

  /** Close open ng-select panels when the Configure Row body (or parent) scrolls. */
  private isDropdownInternalScroll(event: Event): boolean {
    const target = event.target;
    if (!(target instanceof Element)) {
      return false;
    }
    return !!target.closest('.ng-dropdown-panel, .ng-dropdown-panel-items');
  }

  private handlePageScrollClose(event?: Event): void {
    if (event && this.isDropdownInternalScroll(event)) {
      return;
    }
    this.closeDropdownsOnScroll();
  }

  private closeDropdownsOnScroll(): void {
    let closed = false;
    this.pageNgSelects?.forEach((select) => {
      if (select?.isOpen) {
        select.close();
        closed = true;
      }
    });
    if (!closed) {
      return;
    }
    const activeElement = document.activeElement as HTMLElement | null;
    activeElement?.blur?.();
  }

  private attachScrollCloseListeners(): void {
    this.detachScrollCloseListeners();

    const host = this.elementRef?.nativeElement;
    if (!host) {
      return;
    }

    const targets: EventTarget[] = [];
    const seen = new Set<EventTarget>();
    const addTarget = (el: EventTarget | null | undefined): void => {
      if (!el || seen.has(el)) {
        return;
      }
      seen.add(el);
      targets.push(el);
    };

    [window, document, document.documentElement, document.body].forEach((el) => addTarget(el));

    host
      .querySelectorAll('.saru-editor__body, .saru-editor__content, .saru-editor__main, .saru-editor__nav, .modal-body')
      .forEach((el) => addTarget(el));

    let current: HTMLElement | null = host.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY || style.overflow;
      if (/(auto|scroll|overlay)/i.test(overflowY) || current.scrollHeight > current.clientHeight) {
        addTarget(current);
      }
      current = current.parentElement;
    }

    const handler = (event: Event) => this.handlePageScrollClose(event);
    targets.forEach((target) => {
      target.addEventListener('scroll', handler, { passive: true, capture: true } as AddEventListenerOptions);
      this.scrollCloseCleanupFns.push(() => {
        target.removeEventListener('scroll', handler, true);
      });
    });
  }

  private detachScrollCloseListeners(): void {
    while (this.scrollCloseCleanupFns.length > 0) {
      const cleanup = this.scrollCloseCleanupFns.pop();
      cleanup?.();
    }
  }

  setTab(tab: number): void {
    this.activeTab = tab;
    // Re-normalize before ng-select remounts (*ngIf) so chip labels resolve again.
    this.normalizeMultiSelectIds();
    this.hydrateClaimAvailableTags();
    this.refreshSectionErrorBanner();
    if (tab === 10) this.scheduleFssaiTagRefresh();
    this.scheduleLucideIconRender();
    this.cdRef.detectChanges();
  }

  private ensureExtendedSelectionDefaults(): void {
    if (!Array.isArray(this.selections.healthConditionTags)) this.selections.healthConditionTags = [];
    if (!Array.isArray(this.selections.generatedFssaiNutrientTags)) {
      this.selections.generatedFssaiNutrientTags = [];
    }
    if (!Array.isArray(this.selections.productClaims)) this.selections.productClaims = [];
    if (!Array.isArray(this.selections.productBenefitClaims)) this.selections.productBenefitClaims = [];
    if (!Array.isArray(this.selections.servingMeasurements)) this.selections.servingMeasurements = [];
    this.refreshMacroOptions();
  }

  get availableMacroOptions(): Array<{ value: string; label: string }> {
    return this.standardMacroOptions;
  }

  private refreshMacroOptions(): void {
    const used = new Set(
      (this.selections.macronutrients || []).map((m) => String(m.nutrientName || '').toLowerCase())
    );
    this.standardMacroOptions = this.standardMacroLabels
      .filter((l) => !used.has(l.toLowerCase()))
      .map((l) => ({ value: l, label: l }));
  }

  onAddStandardMacro(label: string | { value?: string; label?: string } | null): void {
    const name =
      typeof label === 'string'
        ? label
        : String(label?.value || label?.label || '').trim();
    if (!name) return;
    const match = this.resolveMacroFromLookups(name);
    this.selections.macronutrients.push({
      nutrientId: match ? String(match.id) : '',
      nutrientName: match?.name || name,
      unitId: '',
      unitName: '',
      amount: '',
      percentDV: '',
      rda: '',
    });
    this.selectedMacroToAdd = null;
    this.refreshMacroOptions();
    this.scheduleFssaiTagRefresh();
    this.cdRef.detectChanges();
  }

  private resolveMacroFromLookups(name: string): FpBulkLookupOption | null {
    const list = this.lookups?.nutrients || [];
    if (!list.length) return null;
    const key = String(name || '')
      .toLowerCase()
      .replace(/fibre/g, 'fiber')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    const aliases: Record<string, string[]> = {
      energy: ['energy / calories', 'energy', 'calories'],
      protein: ['protein'],
      carbohydrates: ['carbohydrates', 'carbohydrate', 'total carbohydrate', 'total carbohydrates'],
      'total fat': ['total fat', 'fat'],
      'dietary fiber': ['dietary fiber', 'dietary fibre', 'fiber', 'fibre', 'total dietary fiber'],
      cholesterol: ['cholesterol'],
      sodium: ['sodium', 'salt'],
    };
    const needles = aliases[key] || [key];
    for (const alias of needles) {
      const exact = list.find((o) =>
        String(o.name || '')
          .toLowerCase()
          .replace(/fibre/g, 'fiber')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim() === alias
      );
      if (exact) return exact;
    }
    for (const alias of needles) {
      const fuzzy = list.find((o) => {
        const n = String(o.name || '')
          .toLowerCase()
          .replace(/fibre/g, 'fiber')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
        return n.includes(alias) || alias.includes(n);
      });
      if (fuzzy) return fuzzy;
    }
    return null;
  }

  onMacroUnitChange(index: number): void {
    const row = this.selections.macronutrients[index];
    if (!row) return;
    const match = (this.lookups.units || []).find((u) => String(u.id) === String(row.unitId));
    row.unitName = match?.name || '';
    this.scheduleFssaiTagRefresh();
  }

  scheduleFssaiTagRefresh(): void {
    if (this.fssaiTagTimer) clearTimeout(this.fssaiTagTimer);
    this.fssaiTagTimer = setTimeout(() => void this.refreshFssaiTags(), 400);
  }

  private async refreshFssaiTags(): Promise<void> {
    const form = String(this.selections.productForm || '').trim();
    const macros = this.selections.macronutrients || [];
    const findAmt = (name: string) => {
      const row = macros.find((m) => {
        const n = String(m.nutrientName || '').toLowerCase();
        if (name === 'fat') {
          return (n.includes('total fat') || n === 'fat') && !n.includes('satur') && !n.includes('trans');
        }
        return n.includes(name);
      });
      const n = Number(row?.amount);
      return Number.isFinite(n) ? n : undefined;
    };
    const calories = findAmt('energy') ?? findAmt('calor');
    const protein = findAmt('protein');
    const fat = findAmt('fat');
    const fiber = findAmt('fiber') ?? findAmt('fibre');
    const carbs = findAmt('carb');
    if (!form || calories == null || protein == null || fat == null || fiber == null) {
      this.selections.generatedFssaiNutrientTags = [];
      this.cdRef.markForCheck();
      return;
    }
    const nutrientValues: Record<string, number | null | undefined> = {};
    const nutrientUnits: Record<string, string | null | undefined> = {};
    for (const row of macros) {
      const label = String(row.nutrientName || '').trim();
      const amount = Number(row.amount);
      if (!label || !Number.isFinite(amount)) continue;
      nutrientValues[label] = amount;
      if (row.unitName) nutrientUnits[label] = String(row.unitName);
    }
    this.loadingFssaiTags = true;
    this.cdRef.markForCheck();
    try {
      const res = await this.fssaiTagService
        .calculateTags({
          stateOfProduct: form,
          calories,
          protein,
          fat,
          fiber,
          carbs,
          nutrientValues,
          nutrientUnits,
        })
        .toPromise();
      this.selections.generatedFssaiNutrientTags = this.fssaiTagService
        .extractTagsFromApiResponse(res)
        .map((t) => ({
          fssaiTagName: t.fssaiTagName || '',
          fssaiNutrientTagMasterId: t.fssaiNutrientTagMasterId,
        }))
        .filter((t) => !!t.fssaiTagName);
    } catch {
      this.selections.generatedFssaiNutrientTags = [];
    } finally {
      this.loadingFssaiTags = false;
      this.cdRef.markForCheck();
    }
  }

  get filteredHealthGroups(): FpBulkHealthConditionGroup[] {
    const q = this.healthGroupSearch.trim().toLowerCase();
    const list = this.selections.healthConditionTags || [];
    if (!q) return list;
    return list.filter((g) => g.groupName.toLowerCase().includes(q));
  }

  get availableNutrientGroupsForModal(): FpBulkLookupOption[] {
    const q = this.healthModalSearch.trim().toLowerCase();
    const used = new Set((this.selections.healthConditionTags || []).map((g) => String(g.groupId)));
    return (this.lookups.nutrientGroups || [])
      .filter((g) => !used.has(String(g.id)))
      .filter((g) => !q || String(g.name).toLowerCase().includes(q));
  }

  openHealthGroupModal(): void {
    this.healthModalSearch = '';
    this.healthModalSelection = new Set();
    this.healthModalRef = this.modalService.open(this.healthGroupModalTpl, {
      size: 'md',
      backdrop: 'static',
      centered: true,
      windowClass: 'saru-subdialog-window',
    });
    this.healthModalRef.result.catch(() => {
      this.healthModalRef = null;
    });
    this.scheduleLucideIconRender();
  }

  closeHealthGroupModal(): void {
    try {
      this.healthModalRef?.dismiss();
    } catch {
      /* noop */
    }
    this.healthModalRef = null;
  }

  isHealthModalSelected(id: string | number): boolean {
    return this.healthModalSelection.has(String(id));
  }

  toggleHealthModalSelection(id: string | number): void {
    const key = String(id);
    if (this.healthModalSelection.has(key)) this.healthModalSelection.delete(key);
    else this.healthModalSelection.add(key);
  }

  confirmHealthGroups(): void {
    const tags = this.lookups.nutrientTags || [];
    for (const id of this.healthModalSelection) {
      const group = (this.lookups.nutrientGroups || []).find((g) => String(g.id) === id);
      if (!group) continue;
      if ((this.selections.healthConditionTags || []).some((g) => String(g.groupId) === id)) continue;
      const groupTags = tags
        .filter((t) => String(t.groupId) === id)
        .map((t) => ({ id: String(t.id), name: t.name }));
      this.selections.healthConditionTags.push({
        groupId: String(group.id),
        groupName: group.name,
        tags: groupTags,
      });
    }
    this.closeHealthGroupModal();
  }

  removeHealthGroup(group: FpBulkHealthConditionGroup): void {
    this.selections.healthConditionTags = (this.selections.healthConditionTags || []).filter(
      (g) => g.groupId !== group.groupId
    );
  }

  removeHealthTag(group: FpBulkHealthConditionGroup, tagId: string): void {
    group.tags = (group.tags || []).filter((t) => t.id !== tagId);
  }

  getVisibleHealthTags(group: FpBulkHealthConditionGroup): Array<{ id: string; name: string }> {
    const tags = group.tags || [];
    if (this.isHealthExpanded(group.groupId) || tags.length <= 6) return tags;
    return tags.slice(0, 6);
  }

  isHealthExpanded(groupId: string): boolean {
    return this.healthExpanded.has(groupId);
  }

  toggleHealthExpanded(groupId: string): void {
    if (this.healthExpanded.has(groupId)) this.healthExpanded.delete(groupId);
    else this.healthExpanded.add(groupId);
  }

  getClaimTagsForRow(kind: 'product' | 'benefit', index: number): FpBulkLookupOption[] {
    const row =
      kind === 'product'
        ? this.selections.productClaims[index]
        : this.selections.productBenefitClaims[index];
    if (!row) return [];
    if (!row.availableTags?.length) {
      row.availableTags = this.resolveClaimTagsForType(kind, row.claimTypeId);
    }
    return row.availableTags;
  }

  private resolveClaimTagsForType(
    kind: 'product' | 'benefit',
    claimTypeId: string | number | null | undefined
  ): FpBulkLookupOption[] {
    if (claimTypeId == null || claimTypeId === '') return [];
    const typeKey = String(claimTypeId);
    if (kind === 'product') {
      return (this.lookups.claimTags || []).filter(
        (t) => String(t.claimTypeId ?? '') === typeKey && !!String(t.name || '').trim()
      );
    }
    return (this.lookups.productBenefitTags || []).filter(
      (t) => String(t.productBenefitId ?? '') === typeKey && !!String(t.name || '').trim()
    );
  }

  /** Keep multi-select models as string ids so chips always match lookup items after remount. */
  private normalizeMultiSelectIds(): void {
    const toIds = (values: Array<string | number> | null | undefined): string[] =>
      (values || [])
        .map((v) => {
          if (v != null && typeof v === 'object' && 'id' in (v as object)) {
            return String((v as { id: unknown }).id ?? '');
          }
          return String(v ?? '').trim();
        })
        .filter(Boolean);

    const s = this.selections;
    s.tasteIds = toIds(s.tasteIds);
    s.textureIds = toIds(s.textureIds);
    s.flavourIds = toIds(s.flavourIds);
    s.aromaIds = toIds(s.aromaIds);
    s.cuisineIds = toIds(s.cuisineIds);
    s.occasionIds = toIds(s.occasionIds);
    s.locationIds = toIds(s.locationIds);
    s.seasonIds = toIds(s.seasonIds);
    s.allergenDeclaredIds = toIds(s.allergenDeclaredIds);
    s.mayContainIds = toIds(s.mayContainIds);
    s.producedInFacilityIds = toIds(s.producedInFacilityIds);
    s.allergenContainsIds = toIds(s.allergenContainsIds);
    s.allergenFreeIds = toIds(s.allergenFreeIds);
    s.intoleranceContainsIds = toIds(s.intoleranceContainsIds);
    s.intoleranceFreeIds = toIds(s.intoleranceFreeIds);
    if (s.continentId != null && s.continentId !== '') s.continentId = String(s.continentId);

    (s.productClaims || []).forEach((c) => {
      c.claimTypeId = c.claimTypeId != null && c.claimTypeId !== '' ? String(c.claimTypeId) : '';
      c.selectedTagIds = toIds(c.selectedTagIds);
    });
    (s.productBenefitClaims || []).forEach((c) => {
      c.claimTypeId = c.claimTypeId != null && c.claimTypeId !== '' ? String(c.claimTypeId) : '';
      c.selectedTagIds = toIds(c.selectedTagIds);
    });
  }

  private hydrateClaimAvailableTags(): void {
    (this.selections.productClaims || []).forEach((c) => {
      c.availableTags = this.resolveClaimTagsForType('product', c.claimTypeId);
    });
    (this.selections.productBenefitClaims || []).forEach((c) => {
      c.availableTags = this.resolveClaimTagsForType('benefit', c.claimTypeId);
    });
  }

  /** Resolve chip label when ng-select remounts with id-only model values. */
  resolveOptionLabel(options: FpBulkLookupOption[] | undefined, item: unknown): string {
    if (item == null) return '';
    if (typeof item === 'object') {
      const obj = item as { name?: string; label?: string; id?: unknown };
      if (obj.name) return String(obj.name);
      if (obj.label) return String(obj.label);
      const match = (options || []).find((o) => String(o.id) === String(obj.id ?? ''));
      return match?.name || String(obj.id ?? '');
    }
    const match = (options || []).find((o) => String(o.id) === String(item));
    return match?.name || String(item);
  }

  onClaimTypeChange(kind: 'product' | 'benefit', index: number): void {
    const row =
      kind === 'product'
        ? this.selections.productClaims[index]
        : this.selections.productBenefitClaims[index];
    if (!row) return;
    const list = kind === 'product' ? this.lookups.claimTypes : this.lookups.productBenefits;
    const match = (list || []).find((o) => String(o.id) === String(row.claimTypeId));
    // Keep claimTypeId as selected — do not re-stringify (breaks ng-select tag binding).
    row.claimTypeName = match?.name || '';
    row.selectedTagIds = [];
    row.selectedTagNames = [];
    row.availableTags = this.resolveClaimTagsForType(kind, row.claimTypeId);
  }

  onClaimTagsChange(kind: 'product' | 'benefit', index: number): void {
    const row =
      kind === 'product'
        ? this.selections.productClaims[index]
        : this.selections.productBenefitClaims[index];
    if (!row) return;
    const options = row.availableTags?.length
      ? row.availableTags
      : this.resolveClaimTagsForType(kind, row.claimTypeId);
    // Do not reassign selectedTagIds — new array reference can reset ng-select.
    row.selectedTagNames = (row.selectedTagIds || [])
      .map((id) => options.find((o) => String(o.id) === String(id))?.name || '')
      .filter(Boolean);
  }

  /** ng-select id compare that tolerates string vs number. */
  compareById = (a: unknown, b: unknown): boolean => String(a ?? '') === String(b ?? '');

  hasSectionError(section: number): boolean {
    return Object.keys(this.fieldErrors).some((field) => this.fieldSection(field) === section);
  }

  hasFieldError(field: string): boolean {
    return !!this.fieldErrors[field];
  }

  getFieldError(field: string): string {
    return this.fieldErrors[field] || '';
  }

  clearFieldError(field: string): void {
    if (!this.fieldErrors[field]) return;
    delete this.fieldErrors[field];
    this.refreshSectionErrorBanner();
  }

  /** Show error for a single field only after blur / leave focus. */
  onFieldBlur(field: string): void {
    const err = getFpBulkFieldErrors(this.parsed, this.selections).find((e) => e.field === field);
    if (err) {
      this.fieldErrors[field] = err.message;
    } else {
      delete this.fieldErrors[field];
    }
    this.refreshSectionErrorBanner();
  }

  private refreshSectionErrorBanner(): void {
    this.sectionErrorMessages = Object.entries(this.fieldErrors)
      .filter(([field]) => this.fieldSection(field) === this.activeTab)
      .map(([, msg]) => msg);
  }

  private fieldSection(field: string): number {
    const map: Record<string, number> = {
      companyName: 1,
      address: 1,
      executiveOwner: 1,
      mobileNumber: 1,
      email: 1,
      countryId: 1,
      stateId: 1,
      cityId: 1,
      pincodeId: 1,
      brandName: 2,
      productName: 2,
      healthColorCodeId: 2,
      dietaryPreferenceId: 2,
      productType: 2,
      productForm: 2,
      dataSourceId: 2,
      databaseSourceId: 2,
      countryOfOriginId: 2,
      assignment: 15,
    };
    return map[field] ?? this.activeTab;
  }

  getParsedText(key: string): string {
    return String((this.parsed as any)?.[key] ?? '');
  }

  setParsedText(key: string, value: string): void {
    (this.parsed as any)[key] = value ?? '';
  }

  private matchOptionByName(
    list: Array<{ id: string | number; name: string }> | undefined,
    name?: string | null
  ): { id: string | number; name: string } | null {
    const needle = String(name || '').trim().toLowerCase();
    if (!needle || !list?.length) return null;
    return list.find((o) => String(o.name || '').trim().toLowerCase() === needle) || null;
  }

  private async hydrateCompanyLocationFromCsvHints(): Promise<void> {
    const pin = String(this.selections.pincode || this.parsed.pincode || '').trim();
    if (pin && !this.selections.pincodeId) {
      try {
        const response = await this.masterService.searchLocationsByPincode(pin, 10);
        const items = this.unwrapList(response).map((item) => this.normalizePincodeLookup(item));
        this.cachePincodeLookups(items);
        const exact =
          items.find((i) => String(i.pincode) === pin) ||
          items.find((i) => String(i.pincode).startsWith(pin)) ||
          items[0];
        if (exact?.countryId && exact?.stateId && exact?.cityId) {
          await this.applyCompanyLocationFromPincode(exact);
          return;
        }
      } catch {
        /* fall through to name cascade */
      }
    }

    if (!this.selections.countryId) {
      const country =
        this.matchOptionByName(this.lookups.countries, this.selections.countryName) ||
        this.matchOptionByName(this.lookups.countries, this.parsed.country);
      if (country) {
        this.selections.countryId = String(country.id);
        this.selections.countryName = country.name;
      }
    }

    if (this.selections.countryId) {
      await this.onCompanyCountryChange(false);
      if (!this.selections.stateId && (this.selections.stateName || this.parsed.state)) {
        const state = this.matchOptionByName(
          this.companyStates,
          this.selections.stateName || this.parsed.state
        );
        if (state) {
          this.selections.stateId = String(state.id);
          this.selections.stateName = state.name;
        }
      }
      if (this.selections.stateId) {
        await this.onCompanyStateChange(false);
        if (!this.selections.cityId && (this.selections.cityName || this.parsed.city)) {
          const city = this.matchOptionByName(
            this.companyCities,
            this.selections.cityName || this.parsed.city
          );
          if (city) {
            this.selections.cityId = String(city.id);
            this.selections.cityName = city.name;
          }
        }
        if (this.selections.cityId) {
          await this.onCompanyCityChange(false);
          if (!this.selections.pincodeId && pin) {
            const pinMatch = this.companyPincodeItems.find(
              (i) => String(i.pincode) === pin || String(i.label).startsWith(pin)
            );
            if (pinMatch) {
              this.selections.pincodeId = String(pinMatch.id);
              this.selections.pincode = pinMatch.pincode || pin;
            }
          }
        }
      }
    }
  }

  private async hydrateEntityLocationFromCsvHints(
    key: keyof Pick<
      FpBulkPreviewRow['selections'],
      | 'manufacturedBy'
      | 'marketedBy'
      | 'packagedBy'
      | 'importedBy'
      | 'exportedBy'
      | 'packagedBySection4'
    >
  ): Promise<void> {
    const sel = this.selections[key] as FpBulkEntitySelections;
    if (!sel) return;

    const pin = String(sel.pincode || '').trim();
    if (pin && !sel.pincodeId) {
      try {
        const response = await this.masterService.searchLocationsByPincode(pin, 10);
        const items = this.unwrapList(response).map((item) => this.normalizePincodeLookup(item));
        this.entityPincodeItems[key] = items;
        this.cachePincodeLookups(items);
        const exact =
          items.find((i) => String(i.pincode) === pin) ||
          items.find((i) => String(i.pincode).startsWith(pin)) ||
          items[0];
        if (exact?.countryId && exact?.stateId && exact?.cityId) {
          await this.applyEntityLocationFromPincode(key, exact);
          return;
        }
      } catch {
        /* fall through */
      }
    }

    if (!sel.countryId && sel.countryName) {
      const country = this.matchOptionByName(this.lookups.countries, sel.countryName);
      if (country) {
        sel.countryId = String(country.id);
        sel.countryName = country.name;
      }
    }

    if (sel.countryId) {
      await this.loadEntityStates(key, sel.countryId);
      if (!sel.stateId && sel.stateName) {
        const state = this.matchOptionByName(this.entityStateOptions[key], sel.stateName);
        if (state) {
          sel.stateId = String(state.id);
          sel.stateName = state.name;
        }
      }
      if (sel.stateId) {
        await this.loadEntityCities(key, sel.stateId);
        if (!sel.cityId && sel.cityName) {
          const city = this.matchOptionByName(this.entityCityOptions[key], sel.cityName);
          if (city) {
            sel.cityId = String(city.id);
            sel.cityName = city.name;
          }
        }
        if (sel.cityId) {
          await this.loadEntityPincodes(key, sel.cityId);
          if (!sel.pincodeId && pin) {
            const pinMatch = (this.entityPincodeItems[key] || []).find(
              (i) => String(i.pincode) === pin || String(i.label).startsWith(pin)
            );
            if (pinMatch) {
              sel.pincodeId = String(pinMatch.id);
              sel.pincode = pinMatch.pincode || pin;
            }
          }
        }
      }
    }
  }

  private applyAllValidationErrors(): void {
    const errors = getFpBulkFieldErrors(this.parsed, this.selections);
    this.fieldErrors = {};
    errors.forEach((e) => {
      if (!this.fieldErrors[e.field]) {
        this.fieldErrors[e.field] = e.message;
      }
    });
    this.refreshSectionErrorBanner();
  }

  private unwrapList(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.Data)) return response.Data;
    if (Array.isArray(response?.response)) return response.response;
    if (Array.isArray(response?.Response)) return response.Response;
    const root = response?.response ?? response?.data ?? response ?? {};
    if (Array.isArray(root)) return root;
    return (
      root.items ||
      root.states ||
      root.cities ||
      root.pincodes ||
      root.stateList ||
      root.cityList ||
      root.pincodeList ||
      root.list ||
      []
    );
  }

  private unwrapOptions(response: any): FpBulkLookupOption[] {
    return extractMetadataOptions(this.unwrapList(response))
      .map((o) => ({ id: String(o.id), name: o.name }))
      .filter((o) => o.id && o.name);
  }

  private ensureOption(
    list: FpBulkLookupOption[],
    id?: string | null,
    name?: string | null
  ): FpBulkLookupOption[] {
    if (!id) return list || [];
    const exists = (list || []).some((o) => String(o.id) === String(id));
    if (exists) return list || [];
    return [...(list || []), { id: String(id), name: name || String(id) }];
  }

  private normalizePincodeLookup(raw: any): FpPincodeLookupItem {
    const pincodeId = String(raw?.pincodeId ?? raw?.PincodeId ?? raw?.id ?? '');
    const pincode = String(raw?.pincode ?? raw?.Pincode ?? raw?.name ?? '');
    const cityName = String(raw?.cityName ?? raw?.CityName ?? '');
    const stateName = String(raw?.stateName ?? raw?.StateName ?? '');
    const countryName = String(raw?.countryName ?? raw?.CountryName ?? '');
    const area = String(raw?.area ?? raw?.Area ?? '');
    const areaSuffix = area ? ` (${area})` : '';
    const label =
      raw?.displayLabel ??
      raw?.DisplayLabel ??
      (cityName
        ? `${pincode}${areaSuffix} — ${cityName}, ${stateName}, ${countryName}`
        : `${pincode}${areaSuffix}`);
    return {
      id: pincodeId,
      pincodeId,
      pincode,
      name: pincode || label,
      label,
      area,
      cityId: String(raw?.cityId ?? raw?.CityId ?? ''),
      cityName,
      stateId: String(raw?.stateId ?? raw?.StateId ?? ''),
      stateName,
      countryId: String(raw?.countryId ?? raw?.CountryId ?? ''),
      countryName,
      isLookup: true,
    };
  }

  private cachePincodeLookups(items: FpPincodeLookupItem[]): void {
    items.forEach((item) => {
      const key = String(item.pincodeId || item.id || '');
      if (key) this.pincodeLookupCache.set(key, item);
      if (item.pincode) this.pincodeLookupCache.set(item.pincode, item);
    });
  }

  private initCompanyPincodeTypeahead(): void {
    this.companyPincodeTypeaheadSub = this.companyPincodeTypeahead
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => (term || '').trim().length >= 3),
        switchMap((term) => {
          const query = (term || '').trim();
          this.searchingCompanyPincode = true;
          this.cdRef.markForCheck();
          return from(
            this.masterService
              .searchLocationsByPincode(query, 25)
              .then((response) =>
                this.unwrapList(response).map((item) => this.normalizePincodeLookup(item))
              )
              .catch(() => [] as FpPincodeLookupItem[])
          );
        })
      )
      .subscribe((results) => {
        this.searchingCompanyPincode = false;
        this.companyPincodeItems = results || [];
        this.cachePincodeLookups(this.companyPincodeItems);
        this.cdRef.markForCheck();
      });
  }

  private initEntityPincodeTypeaheads(): void {
    for (const e of this.entityKeys) {
      const key = e.key;
      const subject = new Subject<string>();
      this.entityPincodeTypeahead[key] = subject;
      this.entityPincodeItems[key] = this.entityPincodeItems[key] || [];
      const sub = subject
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          filter((term) => (term || '').trim().length >= 3),
          switchMap((term) => {
            const query = (term || '').trim();
            this.entitySearchingPincode[key] = true;
            this.cdRef.markForCheck();
            return from(
              this.masterService
                .searchLocationsByPincode(query, 25)
                .then((response) =>
                  this.unwrapList(response).map((item) => this.normalizePincodeLookup(item))
                )
                .catch(() => [] as FpPincodeLookupItem[])
            );
          })
        )
        .subscribe((results) => {
          this.entitySearchingPincode[key] = false;
          this.entityPincodeItems[key] = results || [];
          this.cachePincodeLookups(this.entityPincodeItems[key]);
          this.cdRef.markForCheck();
        });
      this.entityPincodeSubs.push(sub);
    }
  }

  async onCompanyCountryChange(resetChildren = true): Promise<void> {
    if (this.applyingPincode) return;
    const countryId = this.selections.countryId;
    const match = this.lookups.countries.find((c) => String(c.id) === String(countryId));
    this.selections.countryName = match?.name || null;
    if (resetChildren) {
      this.selections.stateId = null;
      this.selections.cityId = null;
      this.selections.pincodeId = null;
      this.selections.stateName = null;
      this.selections.cityName = null;
      this.selections.pincode = null;
      this.companyCities = [];
      this.companyPincodeItems = [];
    }
    this.companyStates = [];
    if (!countryId) return;
    this.loadingCompanyStates = true;
    try {
      const res = await this.masterService.getProductStatesByCountry(countryId);
      this.companyStates = this.unwrapOptions(res);
    } catch {
      this.companyStates = [];
    } finally {
      this.loadingCompanyStates = false;
      this.clearFieldError('countryId');
    }
  }

  async onCompanyStateChange(resetChildren = true): Promise<void> {
    if (this.applyingPincode) return;
    const stateId = this.selections.stateId;
    const match = this.companyStates.find((s) => String(s.id) === String(stateId));
    this.selections.stateName = match?.name || null;
    if (resetChildren) {
      this.selections.cityId = null;
      this.selections.pincodeId = null;
      this.selections.cityName = null;
      this.selections.pincode = null;
      this.companyPincodeItems = [];
    }
    this.companyCities = [];
    if (!stateId) return;
    this.loadingCompanyCities = true;
    try {
      const res = await this.masterService.getProductCitiesByState(stateId);
      this.companyCities = this.unwrapOptions(res);
    } catch {
      this.companyCities = [];
    } finally {
      this.loadingCompanyCities = false;
      this.clearFieldError('stateId');
    }
  }

  async onCompanyCityChange(resetChildren = true): Promise<void> {
    if (this.applyingPincode) return;
    const cityId = this.selections.cityId;
    const match = this.companyCities.find((c) => String(c.id) === String(cityId));
    this.selections.cityName = match?.name || null;
    if (resetChildren) {
      this.selections.pincodeId = null;
      this.selections.pincode = null;
    }
    this.companyPincodeItems = [];
    if (!cityId) return;
    this.loadingCompanyPincodes = true;
    try {
      const res = await this.masterService.getProductPincodesByCity(cityId);
      this.companyPincodeItems = this.unwrapList(res).map((item) => {
        const opt = this.normalizePincodeLookup({
          ...item,
          cityId: item.cityId ?? cityId,
          cityName: item.cityName ?? this.selections.cityName,
          stateId: item.stateId ?? this.selections.stateId,
          stateName: item.stateName ?? this.selections.stateName,
          countryId: item.countryId ?? this.selections.countryId,
          countryName: item.countryName ?? this.selections.countryName,
        });
        // City-list items may only have id/name — keep usable label
        if (!opt.pincode) {
          opt.pincode = String(item.name ?? item.pincode ?? '');
          opt.name = opt.pincode;
          opt.label = opt.pincode;
        }
        return opt;
      });
      this.cachePincodeLookups(this.companyPincodeItems);
    } catch {
      this.companyPincodeItems = [];
    } finally {
      this.loadingCompanyPincodes = false;
      this.clearFieldError('cityId');
    }
  }

  onCompanyPincodeOptionSelected(item: any): void {
    if (!item || this.applyingPincode) return;
    const lookup = item.isLookup ? item : this.normalizePincodeLookup(item);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      void this.applyCompanyLocationFromPincode(lookup);
    }
  }

  async onCompanyPincodeSelected(selection: any): Promise<void> {
    if (!selection || this.applyingPincode) return;

    if (typeof selection === 'object') {
      const raw = selection?.value ?? selection;
      const lookupFromItem = raw?.countryId ? raw : this.normalizePincodeLookup(raw);
      if (lookupFromItem?.countryId && lookupFromItem?.stateId && lookupFromItem?.cityId) {
        await this.applyCompanyLocationFromPincode(lookupFromItem);
        return;
      }
      const idFromItem = raw?.pincodeId ?? raw?.id;
      if (idFromItem != null) selection = String(idFromItem);
    }

    const lookup = await this.resolvePincodeLocationLookup(selection);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      await this.applyCompanyLocationFromPincode(lookup);
      return;
    }

    const match = this.companyPincodeItems.find((p) => String(p.id) === String(selection));
    this.selections.pincode = match?.pincode || match?.name || null;
    this.clearFieldError('pincodeId');
  }

  private async resolvePincodeLocationLookup(
    pincodeId: string | number
  ): Promise<FpPincodeLookupItem | null> {
    const key = String(pincodeId).trim();
    if (!key) return null;

    const cached = this.pincodeLookupCache.get(key);
    if (cached?.countryId) return cached;

    const fromItems = this.companyPincodeItems.find(
      (p) => String(p.pincodeId ?? p.id) === key || String(p.pincode) === key
    );
    if (fromItems?.countryId) return fromItems;

    if (!/^\d+$/.test(key)) return null;

    try {
      const response = await this.masterService.getLocationByPincodeId(key);
      const raw =
        response?.pincodeId || response?.PincodeId || response?.pincode
          ? response
          : response?.response ?? response?.data ?? null;
      if (!raw) return null;
      const lookup = this.normalizePincodeLookup(raw);
      if (lookup.countryId) {
        this.pincodeLookupCache.set(String(lookup.pincodeId ?? lookup.id), lookup);
        return lookup;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private async applyCompanyLocationFromPincode(lookup: FpPincodeLookupItem): Promise<void> {
    this.applyingPincode = true;
    try {
      this.lookups.countries = this.ensureOption(
        this.lookups.countries,
        lookup.countryId,
        lookup.countryName
      );

      this.selections.countryId = lookup.countryId;
      this.selections.countryName = lookup.countryName || null;
      this.selections.stateId = lookup.stateId;
      this.selections.stateName = lookup.stateName || null;
      this.selections.cityId = lookup.cityId;
      this.selections.cityName = lookup.cityName || null;
      this.selections.pincodeId = lookup.pincodeId || lookup.id;
      this.selections.pincode = lookup.pincode || null;

      await this.loadCompanyStatesOnly(lookup.countryId);
      this.companyStates = this.ensureOption(this.companyStates, lookup.stateId, lookup.stateName);

      await this.loadCompanyCitiesOnly(lookup.stateId);
      this.companyCities = this.ensureOption(this.companyCities, lookup.cityId, lookup.cityName);

      await this.loadCompanyPincodesOnly(lookup.cityId);
      this.companyPincodeItems = this.mergePincodeItem(this.companyPincodeItems, lookup);
      this.cachePincodeLookups([lookup]);

      this.clearFieldError('countryId');
      this.clearFieldError('stateId');
      this.clearFieldError('cityId');
      this.clearFieldError('pincodeId');
    } finally {
      this.applyingPincode = false;
      this.cdRef.detectChanges();
    }
  }

  private mergePincodeItem(
    list: FpPincodeLookupItem[],
    lookup: FpPincodeLookupItem
  ): FpPincodeLookupItem[] {
    const id = String(lookup.pincodeId || lookup.id);
    const without = (list || []).filter((p) => String(p.id) !== id);
    return [lookup, ...without];
  }

  private async loadCompanyStatesOnly(countryId: string): Promise<void> {
    this.loadingCompanyStates = true;
    try {
      const res = await this.masterService.getProductStatesByCountry(countryId);
      this.companyStates = this.unwrapOptions(res);
    } catch {
      this.companyStates = [];
    } finally {
      this.loadingCompanyStates = false;
    }
  }

  private async loadCompanyCitiesOnly(stateId: string): Promise<void> {
    this.loadingCompanyCities = true;
    try {
      const res = await this.masterService.getProductCitiesByState(stateId);
      this.companyCities = this.unwrapOptions(res);
    } catch {
      this.companyCities = [];
    } finally {
      this.loadingCompanyCities = false;
    }
  }

  private async loadCompanyPincodesOnly(cityId: string): Promise<void> {
    this.loadingCompanyPincodes = true;
    try {
      const res = await this.masterService.getProductPincodesByCity(cityId);
      this.companyPincodeItems = this.unwrapList(res).map((item) =>
        this.normalizePincodeLookup({
          ...item,
          cityId: item.cityId ?? cityId,
          cityName: item.cityName ?? this.selections.cityName,
          stateId: item.stateId ?? this.selections.stateId,
          stateName: item.stateName ?? this.selections.stateName,
          countryId: item.countryId ?? this.selections.countryId,
          countryName: item.countryName ?? this.selections.countryName,
        })
      );
    } catch {
      this.companyPincodeItems = [];
    } finally {
      this.loadingCompanyPincodes = false;
    }
  }

  async onEntityCountryChange(key: string): Promise<void> {
    if (this.applyingPincode) return;
    const sel = (this.selections as any)[key] as FpBulkEntitySelections;
    const match = this.lookups.countries.find((c) => String(c.id) === String(sel.countryId));
    sel.countryName = match?.name || null;
    sel.stateId = null;
    sel.cityId = null;
    sel.pincodeId = null;
    sel.stateName = null;
    sel.cityName = null;
    sel.pincode = null;
    this.entityCityOptions[key] = [];
    this.entityPincodeItems[key] = [];
    await this.loadEntityStates(key, sel.countryId);
  }

  async onEntityStateChange(key: string): Promise<void> {
    if (this.applyingPincode) return;
    const sel = (this.selections as any)[key] as FpBulkEntitySelections;
    const match = (this.entityStateOptions[key] || []).find((s) => String(s.id) === String(sel.stateId));
    sel.stateName = match?.name || null;
    sel.cityId = null;
    sel.pincodeId = null;
    sel.cityName = null;
    sel.pincode = null;
    this.entityPincodeItems[key] = [];
    await this.loadEntityCities(key, sel.stateId);
  }

  async onEntityCityChange(key: string): Promise<void> {
    if (this.applyingPincode) return;
    const sel = (this.selections as any)[key] as FpBulkEntitySelections;
    const match = (this.entityCityOptions[key] || []).find((c) => String(c.id) === String(sel.cityId));
    sel.cityName = match?.name || null;
    sel.pincodeId = null;
    sel.pincode = null;
    await this.loadEntityPincodes(key, sel.cityId);
  }

  onEntityPincodeOptionSelected(key: string, item: any): void {
    if (!item || this.applyingPincode) return;
    const lookup = item.isLookup ? item : this.normalizePincodeLookup(item);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      void this.applyEntityLocationFromPincode(key, lookup);
    }
  }

  async onEntityPincodeSelected(key: string, selection: any): Promise<void> {
    if (!selection || this.applyingPincode) return;

    if (typeof selection === 'object') {
      const raw = selection?.value ?? selection;
      const lookupFromItem = raw?.countryId ? raw : this.normalizePincodeLookup(raw);
      if (lookupFromItem?.countryId && lookupFromItem?.stateId && lookupFromItem?.cityId) {
        await this.applyEntityLocationFromPincode(key, lookupFromItem);
        return;
      }
      const idFromItem = raw?.pincodeId ?? raw?.id;
      if (idFromItem != null) selection = String(idFromItem);
    }

    const fromItems = (this.entityPincodeItems[key] || []).find(
      (p) => String(p.pincodeId ?? p.id) === String(selection)
    );
    if (fromItems?.countryId && fromItems?.stateId && fromItems?.cityId) {
      await this.applyEntityLocationFromPincode(key, fromItems);
      return;
    }

    const lookup = await this.resolvePincodeLocationLookup(selection);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      await this.applyEntityLocationFromPincode(key, lookup);
      return;
    }

    const sel = (this.selections as any)[key] as FpBulkEntitySelections;
    sel.pincode = fromItems?.pincode || fromItems?.name || null;
  }

  private async applyEntityLocationFromPincode(
    key: string,
    lookup: FpPincodeLookupItem
  ): Promise<void> {
    this.applyingPincode = true;
    try {
      const sel = (this.selections as any)[key] as FpBulkEntitySelections;
      this.lookups.countries = this.ensureOption(
        this.lookups.countries,
        lookup.countryId,
        lookup.countryName
      );

      sel.countryId = lookup.countryId;
      sel.countryName = lookup.countryName || null;
      sel.stateId = lookup.stateId;
      sel.stateName = lookup.stateName || null;
      sel.cityId = lookup.cityId;
      sel.cityName = lookup.cityName || null;
      sel.pincodeId = lookup.pincodeId || lookup.id;
      sel.pincode = lookup.pincode || null;

      await this.loadEntityStates(key, lookup.countryId);
      this.entityStateOptions[key] = this.ensureOption(
        this.entityStateOptions[key] || [],
        lookup.stateId,
        lookup.stateName
      );

      await this.loadEntityCities(key, lookup.stateId);
      this.entityCityOptions[key] = this.ensureOption(
        this.entityCityOptions[key] || [],
        lookup.cityId,
        lookup.cityName
      );

      await this.loadEntityPincodes(key, lookup.cityId);
      this.entityPincodeItems[key] = this.mergePincodeItem(
        this.entityPincodeItems[key] || [],
        lookup
      );
      this.cachePincodeLookups([lookup]);
    } finally {
      this.applyingPincode = false;
      this.cdRef.detectChanges();
    }
  }

  private async loadEntityStates(key: string, countryId?: string | null): Promise<void> {
    this.entityStateOptions[key] = [];
    if (!countryId) return;
    try {
      const res = await this.masterService.getProductStatesByCountry(countryId);
      this.entityStateOptions[key] = this.unwrapOptions(res);
    } catch {
      this.entityStateOptions[key] = [];
    }
  }

  private async loadEntityCities(key: string, stateId?: string | null): Promise<void> {
    this.entityCityOptions[key] = [];
    if (!stateId) return;
    try {
      const res = await this.masterService.getProductCitiesByState(stateId);
      this.entityCityOptions[key] = this.unwrapOptions(res);
    } catch {
      this.entityCityOptions[key] = [];
    }
  }

  private async loadEntityPincodes(key: string, cityId?: string | null): Promise<void> {
    this.entityPincodeItems[key] = [];
    if (!cityId) return;
    try {
      const res = await this.masterService.getProductPincodesByCity(cityId);
      const sel = (this.selections as any)[key] as FpBulkEntitySelections;
      this.entityPincodeItems[key] = this.unwrapList(res).map((item) =>
        this.normalizePincodeLookup({
          ...item,
          cityId: item.cityId ?? cityId,
          cityName: item.cityName ?? sel?.cityName,
          stateId: item.stateId ?? sel?.stateId,
          stateName: item.stateName ?? sel?.stateName,
          countryId: item.countryId ?? sel?.countryId,
          countryName: item.countryName ?? sel?.countryName,
        })
      );
      this.cachePincodeLookups(this.entityPincodeItems[key]);
    } catch {
      this.entityPincodeItems[key] = [];
    }
  }

  onMasterSelect(
    field:
      | 'healthColorCodeId'
      | 'dietaryPreferenceId'
      | 'dataSourceId'
      | 'databaseSourceId'
      | 'countryOfOriginId'
      | 'fssaiCategoryId'
      | 'eStoreCategoryId'
      | 'reworkSuggestionId',
    list: FpBulkLookupOption[] | FpBulkLookups['healthColorCodes']
  ): void {
    const id = (this.selections as any)[field];
    const match = (list || []).find((o) => String(o.id) === String(id));
    if (field === 'fssaiCategoryId') this.selections.fssaiCategoryName = match?.name || null;
    if (field === 'eStoreCategoryId') this.selections.eStoreCategoryName = match?.name || null;
    if (field === 'reworkSuggestionId') this.selections.reworkSuggestionName = match?.name || null;
    this.clearFieldError(field);
  }

  onContinentChange(): void {
    const match = this.lookups.continents.find((c) => String(c.id) === String(this.selections.continentId));
    this.selections.continentName = match?.name || null;
  }

  onParsedChange(field: string): void {
    this.clearFieldError(field);
  }

  addSkuRow(): void {
    this.selections.skuDetails.push({
      name: '',
      netWeight: '',
      mrp: '',
      manufacturedBy: '',
      marketedBy: '',
      packagedBy: '',
      unitId: null,
      unitName: null,
      countryId: null,
      countryName: null,
      productImage: null,
      productBarcode: null,
    });
  }

  removeSkuRow(index: number): void {
    this.selections.skuDetails.splice(index, 1);
  }

  onSkuUnitChange(sku: FpBulkSkuDraft): void {
    const match = this.lookups.units.find((u) => String(u.id) === String(sku.unitId));
    sku.unitName = match?.name || null;
  }

  onSkuCountryChange(sku: FpBulkSkuDraft): void {
    const match = this.lookups.countries.find((c) => String(c.id) === String(sku.countryId));
    sku.countryName = match?.name || null;
  }

  addServingMeasurement(): void {
    void this.openUomDialog();
  }

  removeServingMeasurement(index: number): void {
    this.selections.servingMeasurements.splice(index, 1);
    this.syncUomCategorySelection();
  }

  removeServingMeasurementRow(row: FpBulkServingMeasurement): void {
    const idx = this.selections.servingMeasurements.indexOf(row);
    if (idx >= 0) this.removeServingMeasurement(idx);
  }

  get filteredServingMeasurements(): FpBulkServingMeasurement[] {
    const list = this.selections.servingMeasurements || [];
    if (!this.selectedUomFilter) return list;
    return list.filter(
      (r) => String(r.servingMeasure || r.categoryName || '') === this.selectedUomFilter
    );
  }

  get uomFilterChips(): string[] {
    const names = (this.selections.servingMeasurements || [])
      .map((r) => String(r.servingMeasure || r.categoryName || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }

  setUomFilter(name: string | null): void {
    this.selectedUomFilter = name;
  }

  async openUomDialog(): Promise<void> {
    if (!this.productUomCategories.length) {
      this.productUomLoading = true;
      try {
        const res: any = await this.masterService.getRecipeUomCategories();
        const list = res?.data || res?.Data || res?.response || [];
        this.productUomCategories = (Array.isArray(list) ? list : []).map((c: any) => ({
          id: Number(c.id ?? c.Id),
          name: String(c.name ?? c.Name ?? ''),
          selected: false,
          adding: false,
        }));
      } catch {
        this.productUomCategories = [];
      } finally {
        this.productUomLoading = false;
      }
    }
    this.syncUomCategorySelection();
    this.uomModalRef = this.modalService.open(this.uomDialogTpl, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
      windowClass: 'saru-subdialog-window saru-subdialog-window--uom',
    });
    this.uomModalRef.result.catch(() => {
      this.uomModalRef = null;
    });
    this.scheduleLucideIconRender();
  }

  closeUomDialog(): void {
    try {
      this.uomModalRef?.close();
    } catch {
      /* noop */
    }
    this.uomModalRef = null;
  }

  private syncUomCategorySelection(): void {
    this.productUomCategories.forEach((cat) => {
      cat.selected = (this.selections.servingMeasurements || []).some(
        (row) =>
          String(row.categoryId) === String(cat.id) ||
          row.servingMeasure === cat.name ||
          row.categoryName === cat.name
      );
    });
  }

  async onUomCategoryToggle(cat: {
    id: number;
    name: string;
    selected: boolean;
    adding: boolean;
  }): Promise<void> {
    const willSelect = !cat.selected;
    cat.selected = willSelect;
    if (!willSelect) {
      this.selections.servingMeasurements = (this.selections.servingMeasurements || []).filter(
        (row) =>
          String(row.categoryId) !== String(cat.id) &&
          row.categoryName !== cat.name &&
          row.servingMeasure !== cat.name
      );
      if (this.selectedUomFilter === cat.name) this.selectedUomFilter = null;
      this.cdRef.markForCheck();
      return;
    }
    cat.adding = true;
    try {
      const res: any = await this.masterService.getRecipeUomMasterByCategory(cat.id);
      const rows = res?.data || res?.Data || res?.response || [];
      for (const uom of Array.isArray(rows) ? rows : []) {
        const uomId = String(uom.id ?? uom.Id ?? '');
        if (
          uomId &&
          (this.selections.servingMeasurements || []).some((r) => String(r.uomId) === uomId)
        ) {
          continue;
        }
        const gram = Number(uom.gram ?? uom.Gram ?? 0) || 0;
        const ml = Number(uom.ml ?? uom.Ml ?? 0) || 0;
        this.selections.servingMeasurements.push({
          qty: 1,
          servingSize: String(uom.subcategoryName || uom.SubcategoryName || uom.name || 'Standard'),
          servingMeasure: cat.name,
          amount: gram > 0 ? gram : ml > 0 ? ml : 100,
          unit: gram > 0 ? 'gm' : ml > 0 ? 'ml' : 'gm',
          calories: 0,
          carbs: 0,
          protein: 0,
          fat: 0,
          fiber: 0,
          uomId: uomId || null,
          categoryId: String(uom.categoryId ?? cat.id),
          subcategoryId: uom.subcategoryId != null ? String(uom.subcategoryId) : null,
          categoryName: cat.name,
          subcategoryName: String(uom.subcategoryName || ''),
        });
      }
      this.selectedUomFilter = cat.name;
    } catch (err) {
      console.error('Failed to add UOM category', err);
      cat.selected = false;
    } finally {
      cat.adding = false;
      this.cdRef.markForCheck();
      this.scheduleLucideIconRender();
    }
  }

  getUomCardTheme(
    index: number,
    name: string
  ): { iconKey: string; iconBg: string; iconColor: string; borderColor: string; iconText?: string } {
    const normalized = (name || '').toLowerCase();
    const themed: Array<{
      match: RegExp;
      iconKey: string;
      iconBg: string;
      iconColor: string;
      borderColor: string;
      iconText?: string;
    }> = [
      { match: /soup bowl/, iconKey: 'soup-bowl', iconBg: '#EEF2FF', iconColor: '#4F46E5', borderColor: '#C7D2FE' },
      { match: /bowl|katori|vati/, iconKey: 'bowl', iconBg: '#EEF2FF', iconColor: '#6D35E8', borderColor: '#CDBDFF' },
      { match: /glass/, iconKey: 'glass', iconBg: '#E0F2FE', iconColor: '#0288D1', borderColor: '#98D8FF' },
      { match: /tablespoon|tbsp/, iconKey: 'tablespoon', iconBg: '#FFF7ED', iconColor: '#EA580C', borderColor: '#FDBA74' },
      { match: /teaspoon|tsp/, iconKey: 'teaspoon', iconBg: '#FFF4E5', iconColor: '#F97316', borderColor: '#FFD59A' },
      { match: /cup/, iconKey: 'cup', iconBg: '#FFF7ED', iconColor: '#EA580C', borderColor: '#FED7AA' },
      { match: /slice/, iconKey: 'slice', iconBg: '#FFF1F2', iconColor: '#EC4899', borderColor: '#FBCFE8' },
      { match: /piece/, iconKey: 'piece', iconBg: '#F5F3FF', iconColor: '#7C3AED', borderColor: '#DDD6FE' },
      { match: /serving/, iconKey: 'serving', iconBg: '#F3E8FF', iconColor: '#8B5CF6', borderColor: '#D8B4FE' },
      {
        match: /number/,
        iconKey: 'numbers',
        iconBg: '#F3E8FF',
        iconColor: '#7C3AED',
        borderColor: '#D8B4FE',
        iconText: '123',
      },
      { match: /gram|kg|mg/, iconKey: 'scale', iconBg: '#ECFDF5', iconColor: '#16A34A', borderColor: '#BBF7D0' },
    ];
    const found = themed.find((item) => item.match.test(normalized));
    if (found) return found;
    const fallback = [
      { iconKey: 'bowl', iconBg: '#EEF2FF', iconColor: '#6D35E8', borderColor: '#CDBDFF' },
      { iconKey: 'glass', iconBg: '#E0F2FE', iconColor: '#0288D1', borderColor: '#98D8FF' },
    ];
    return fallback[index % fallback.length];
  }

  getUomCardIconMarkup(theme: {
    iconKey: string;
    iconText?: string;
  }): SafeHtml | string {
    if (theme.iconText) return theme.iconText;
    return this.sanitizer.bypassSecurityTrustHtml(this.getUomCardSvg(theme.iconKey));
  }

  getMeasurementUomIconMarkup(row: FpBulkServingMeasurement): SafeHtml | string {
    const name = row?.servingMeasure || row?.categoryName || '';
    const idx = this.productUomCategories.findIndex((c) => c.name === name);
    return this.getUomCardIconMarkup(this.getUomCardTheme(idx >= 0 ? idx : 0, name));
  }

  getMeasurementUomTheme(row: FpBulkServingMeasurement) {
    const name = row?.servingMeasure || row?.categoryName || '';
    const idx = this.productUomCategories.findIndex((c) => c.name === name);
    return this.getUomCardTheme(idx >= 0 ? idx : 0, name);
  }

  private getUomCardSvg(iconKey: string): string {
    const common =
      'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';
    const svgs: Record<string, string> = {
      bowl: `<svg ${common}><path d="M4 11c0 4.4 3.6 8 8 8s8-3.6 8-8"/><path d="M5 11h14"/><path d="M7 17c1.4 1 3.2 1.5 5 1.5s3.6-.5 5-1.5"/></svg>`,
      'soup-bowl': `<svg ${common}><path d="M4 11c0 4.4 3.6 8 8 8s8-3.6 8-8"/><path d="M6 11h12"/><path d="M7 17c1.2.8 2.8 1.2 5 1.2s3.8-.4 5-1.2"/><path d="M8 8c.8-.8 1.7-1.2 2.6-1.2"/></svg>`,
      glass: `<svg ${common}><path d="M7 4h10l-1 16H8L7 4z"/><path d="M8 10h8"/></svg>`,
      cup: `<svg ${common}><path d="M6 8h10v7a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V8z"/><path d="M16 9h2a2 2 0 0 1 0 4h-1"/></svg>`,
      tablespoon: `<svg ${common}><path d="M10.2 6.2c1.8 1.8 2.3 4.1 1.1 5.3-1.2 1.2-3.5.7-5.3-1.1s-2.3-4.1-1.1-5.3c1.2-1.2 3.5-.7 5.3 1.1Z"/><path d="M12.1 11.4 19 18.3"/></svg>`,
      teaspoon: `<svg ${common}><path d="M10 7c1.4 1.4 1.8 3.1.8 4.1s-2.7.6-4.1-.8-1.8-3.1-.8-4.1 2.7-.6 4.1.8Z"/><path d="M11 11l7.2 7.2"/></svg>`,
      slice: `<svg ${common}><path d="M5 18l14-7-8-6-6 13z"/><path d="M11 5l-2 9"/></svg>`,
      piece: `<svg ${common}><rect x="6" y="6" width="12" height="12" rx="2"/><path d="M10 10h4v4h-4z"/></svg>`,
      serving: `<svg ${common}><path d="M5 12h14"/><path d="M7 8h10l-1 5H8L7 8z"/><path d="M6 12c0 2.7 2.7 5 6 5s6-2.3 6-5"/></svg>`,
      numbers: `<svg ${common}><path d="M7 8h10"/><path d="M7 16h10"/><path d="M9 5v14"/><path d="M15 5v14"/></svg>`,
      scale: `<svg ${common}><path d="M7 6h10"/><path d="M8 6v12h8V6"/><path d="M9.5 10.5h5"/></svg>`,
    };
    return svgs[iconKey] || svgs['bowl'];
  }

  addMacronutrient(): void {
    const row: FpBulkNutrientItem = {
      nutrientId: '',
      nutrientName: '',
      unitId: '',
      unitName: '',
      amount: '',
      percentDV: '',
      rda: '',
    };
    this.selections.macronutrients.push(row);
  }

  removeMacronutrient(index: number): void {
    this.selections.macronutrients.splice(index, 1);
    this.refreshMacroOptions();
    this.scheduleFssaiTagRefresh();
  }

  addClaim(kind: 'product' | 'benefit' | 'ingredient'): void {
    const row: FpBulkClaimDraft = {
      claimTypeId: '',
      claimTypeName: '',
      claimText: '',
      selectedTagIds: [],
      selectedTagNames: [],
      availableTags: [],
      isDone: false,
    };
    if (kind === 'product') this.selections.productClaims.push(row);
    else if (kind === 'benefit') this.selections.productBenefitClaims.push(row);
    else this.selections.ingredientClaims.push(row);
  }

  removeClaim(kind: 'product' | 'benefit' | 'ingredient', index: number): void {
    if (kind === 'product') this.selections.productClaims.splice(index, 1);
    else if (kind === 'benefit') this.selections.productBenefitClaims.splice(index, 1);
    else this.selections.ingredientClaims.splice(index, 1);
  }

  private initIngredientTypeahead(): void {
    this.ingredientTypeaheadSub = this.ingredientTypeahead
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => (term || '').trim().length >= 2),
        switchMap((term) => {
          const query = (term || '').trim();
          this.ingredientSearchLoading = true;
          this.cdRef.markForCheck();
          return from(this.fetchIngredientSearchResults(query));
        })
      )
      .subscribe((results) => {
        this.ingredientSearchLoading = false;
        this.ingredientSearchResults = this.mergeIngredientSearchWithSelection(results || []);
        this.cdRef.detectChanges();
      });
  }

  private async fetchIngredientSearchResults(
    query: string
  ): Promise<Array<{ id: number; name: string }>> {
    try {
      const res: any = await this.masterService.searchIngredientsForRecipe(query);
      const list = this.extractIngredientSearchRecords(res);
      return list
        .map((i: any) => ({
          id: Number(
            i.id ??
              i.Id ??
              i.ingredientId ??
              i.IngredientId ??
              i.ingredientRefId ??
              i.IngredientRefId ??
              i.ingredientMasterId ??
              i.IngredientMasterId
          ),
          name: String(
            i.ingredientName ||
              i.IngredientName ||
              i.commonName ||
              i.CommonName ||
              i.name ||
              i.Name ||
              'Unknown'
          ).trim(),
        }))
        .filter((i) => Number.isFinite(i.id) && i.id > 0 && !!i.name);
    } catch {
      return [];
    }
  }

  private mergeIngredientSearchWithSelection(
    results: Array<{ id: number; name: string }>
  ): Array<{ id: number; name: string }> {
    const list = [...(results || [])];
    if (
      this.selectedIngredientId != null &&
      this.selectedIngredientName &&
      !list.some((i) => Number(i.id) === Number(this.selectedIngredientId))
    ) {
      list.unshift({
        id: Number(this.selectedIngredientId),
        name: this.selectedIngredientName,
      });
    }
    return list;
  }

  private extractIngredientSearchRecords(res: any): any[] {
    const payload =
      res?.data ??
      res?.Data ??
      (res?.response && typeof res.response === 'object' && !Array.isArray(res.response)
        ? res.response.data ?? res.response.Data ?? res.response
        : res?.response) ??
      res;
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const nested =
      payload.records ??
      payload.Records ??
      payload.items ??
      payload.Items ??
      payload.ingredients ??
      payload.Ingredients ??
      payload.results ??
      payload.Results ??
      payload.list ??
      payload.List ??
      payload.data ??
      payload.Data;
    if (Array.isArray(nested)) return nested;
    for (const key of Object.keys(payload)) {
      if (Array.isArray((payload as any)[key])) return (payload as any)[key];
    }
    return [];
  }

  private getDefaultIngredientUnits(): Array<{
    servingUnitMasterId: string;
    displayLabel: string;
  }> {
    return [
      { servingUnitMasterId: 'pct', displayLabel: '%' },
      { servingUnitMasterId: 'g', displayLabel: 'g' },
      { servingUnitMasterId: 'kg', displayLabel: 'kg' },
      { servingUnitMasterId: 'mg', displayLabel: 'mg' },
      { servingUnitMasterId: 'ml', displayLabel: 'ml' },
      { servingUnitMasterId: 'l', displayLabel: 'L' },
      { servingUnitMasterId: 'mcg', displayLabel: 'mcg' },
      { servingUnitMasterId: 'tsp', displayLabel: 'tsp' },
      { servingUnitMasterId: 'tbsp', displayLabel: 'tbsp' },
    ];
  }

  private seedDefaultIngredientUnits(preferLabel?: string): void {
    const prefer = String(preferLabel || '').trim();
    const merged = [...this.ingredientServingUnits];
    const hasLabel = (label: string) =>
      merged.some(
        (u) =>
          String(u.displayLabel || '').trim().toLowerCase() === label.toLowerCase() ||
          String(u.servingUnitMasterId || '').trim().toLowerCase() === label.toLowerCase()
      );

    // Prefer master units from lookups so save can persist a real unit id.
    for (const u of this.lookups?.units || []) {
      const id = String(u.id ?? '').trim();
      const name = String(u.name || '').trim();
      if (!id || !name) continue;
      if (!hasLabel(name) && !merged.some((x) => String(x.servingUnitMasterId) === id)) {
        merged.push({ servingUnitMasterId: id, displayLabel: name });
      }
    }

    for (const d of this.getDefaultIngredientUnits()) {
      if (!hasLabel(d.displayLabel)) {
        merged.push(d);
      }
    }

    if (prefer && prefer !== '—' && !hasLabel(prefer)) {
      const resolved = (this.lookups?.units || []).find(
        (u) => String(u.name || '').trim().toLowerCase() === prefer.toLowerCase()
      );
      merged.unshift({
        servingUnitMasterId: resolved ? String(resolved.id) : prefer.toLowerCase() === '%' ? 'pct' : `csv_${prefer}`,
        displayLabel: resolved?.name || prefer,
      });
    }

    this.ingredientServingUnits = merged;

    if (prefer && prefer !== '—') {
      const match = this.ingredientServingUnits.find(
        (u) =>
          String(u.displayLabel || '').trim().toLowerCase() === prefer.toLowerCase() ||
          String(u.servingUnitMasterId || '').trim().toLowerCase() === prefer.toLowerCase() ||
          (prefer === '%' && String(u.servingUnitMasterId) === 'pct')
      );
      if (match) {
        this.selectedServingUnitId = match.servingUnitMasterId;
      }
    }
  }

  onUnitSearch(event: { term?: string } | string): void {
    this.pendingUnitSearchTerm =
      typeof event === 'string' ? event : String(event?.term || '');
  }

  addCustomIngredientUnit = (term: string): { servingUnitMasterId: string; displayLabel: string } | null => {
    const label = String(term || '').trim();
    if (!label) return null;
    const resolved = (this.lookups?.units || []).find(
      (u) => String(u.name || '').trim().toLowerCase() === label.toLowerCase()
    );
    if (resolved) {
      return { servingUnitMasterId: String(resolved.id), displayLabel: resolved.name };
    }
    return {
      servingUnitMasterId: `custom_${label.toLowerCase().replace(/\s+/g, '_')}`,
      displayLabel: label,
    };
  };

  openIngredientDialog(editIndex?: number): void {
    this.ingredientSearchResults = [];
    this.selectedIngredientId = null;
    this.selectedIngredientName = '';
    this.ingredientServingUnits = [];
    this.selectedServingUnitId = null;
    this.ingredientAmount = null;
    this.pendingUnitSearchTerm = '';
    this.pendingIngredientTaxonomy = null;

    if (editIndex != null && this.selections.ingredients[editIndex]) {
      this.ingredientEditIndex = editIndex;
      const existing = this.selections.ingredients[editIndex];
      const parsedId = Number(existing.ingredientId);
      const hasMasterId = Number.isFinite(parsedId) && parsedId > 0;
      // Use -1 for CSV/unbound rows — ng-select treats 0 as empty.
      this.selectedIngredientId = hasMasterId ? parsedId : -1;
      this.selectedIngredientName = String(existing.name || '').trim();

      const rawAmount = existing.amount;
      const amt = Number(rawAmount);
      this.ingredientAmount =
        rawAmount !== null &&
        rawAmount !== undefined &&
        String(rawAmount).trim() !== '' &&
        Number.isFinite(amt)
          ? amt
          : null;

      // Seed ng-select so CSV / unbound ingredient names are visible on edit.
      if (this.selectedIngredientName) {
        this.ingredientSearchResults = [
          { id: this.selectedIngredientId!, name: this.selectedIngredientName },
        ];
      }

      const rawUnit = String(existing.unit || '').trim();
      const unitLabel =
        rawUnit && rawUnit !== '—'
          ? this.formatIngredientUnit(rawUnit) || rawUnit
          : '';
      const existingUnitId = String(existing.servingUnitMasterId || '').trim();

      if (hasMasterId) {
        if (existingUnitId) {
          this.selectedServingUnitId = existingUnitId;
        }
        void this.loadIngredientUnits(parsedId).then(() => {
          this.seedDefaultIngredientUnits(unitLabel);
          this.ensureIngredientUnitOption(existingUnitId || null, unitLabel);
          this.cdRef.detectChanges();
        });
      } else {
        this.seedDefaultIngredientUnits(unitLabel);
        this.ensureIngredientUnitOption(existingUnitId || null, unitLabel);
      }

      // Prefill search list from typed/imported ingredient name.
      if (this.selectedIngredientName.length >= 2) {
        void this.searchIngredientsByTerm(this.selectedIngredientName);
      }
    } else {
      this.ingredientEditIndex = null;
      this.seedDefaultIngredientUnits();
    }

    this.ingredientModalRef = this.modalService.open(this.ingredientDialogTpl, {
      size: 'md',
      backdrop: 'static',
      centered: true,
      windowClass: 'saru-subdialog-window',
    });
    this.ingredientModalRef.result.catch(() => {
      this.ingredientEditIndex = null;
      this.ingredientModalRef = null;
    });
    this.scheduleLucideIconRender();
    this.cdRef.detectChanges();
  }

  closeIngredientDialog(): void {
    this.ingredientModalRef?.dismiss();
    this.ingredientModalRef = null;
    this.ingredientEditIndex = null;
  }

  private async searchIngredientsByTerm(term: string): Promise<void> {
    const query = String(term || '').trim();
    if (query.length < 2) return;
    this.ingredientSearchLoading = true;
    this.cdRef.detectChanges();
    const results = await this.fetchIngredientSearchResults(query);
    this.ingredientSearchLoading = false;
    this.ingredientSearchResults = this.mergeIngredientSearchWithSelection(results);
    this.cdRef.detectChanges();
  }

  async onIngredientPicked(item: any): Promise<void> {
    const id = typeof item === 'object' ? Number(item?.id ?? item?.value) : Number(item);
    if (!id || id <= 0) {
      if (typeof item === 'object' && item?.name) {
        this.selectedIngredientName = String(item.name).trim();
      }
      this.pendingIngredientTaxonomy = null;
      this.seedDefaultIngredientUnits(
        this.ingredientServingUnits.find(
          (u) => String(u.servingUnitMasterId) === String(this.selectedServingUnitId)
        )?.displayLabel
      );
      return;
    }
    this.selectedIngredientId = id;
    const found = this.ingredientSearchResults.find((i) => Number(i.id) === id);
    this.selectedIngredientName = found?.name || this.selectedIngredientName || '';
    this.selectedServingUnitId = null;
    await this.loadIngredientUnits(id);
    if (!this.ingredientServingUnits.length) {
      this.seedDefaultIngredientUnits();
    }
    this.pendingIngredientTaxonomy = await this.fetchIngredientTaxonomy(id);
  }

  private ensureIngredientUnitOption(
    id: string | number | null | undefined,
    label: string
  ): void {
    const unitLabel = String(label || '').trim();
    if (!unitLabel || unitLabel === '—') return;

    const sid =
      id != null && String(id).trim() !== ''
        ? String(id).trim()
        : unitLabel.toLowerCase() === '%'
          ? 'pct'
          : `csv_${unitLabel}`;
    const exists = this.ingredientServingUnits.some(
      (u) =>
        String(u.servingUnitMasterId) === sid ||
        String(u.displayLabel || '').trim().toLowerCase() === unitLabel.toLowerCase()
    );
    if (!exists) {
      this.ingredientServingUnits = [
        { servingUnitMasterId: sid, displayLabel: unitLabel },
        ...this.ingredientServingUnits,
      ];
    }
    if (this.selectedServingUnitId == null || this.selectedServingUnitId === '') {
      this.selectedServingUnitId = sid;
    }
  }

  private async loadIngredientUnits(ingredientId: number): Promise<void> {
    try {
      const res: any = await this.masterService.getIngredientServingUnits(ingredientId);
      const raw = res?.data ?? res?.Data ?? this.extractIngredientSearchRecords(res);
      this.ingredientServingUnits = (Array.isArray(raw) ? raw : [])
        .map((u: any) => {
          const servingUnitMasterId =
            u.servingUnitMasterId ?? u.ServingUnitMasterId ?? u.id ?? u.Id ?? u.unitId ?? u.UnitId;
          const composed =
            u.size || u.unit
              ? `${u.size || ''} - ${u.unit || ''} - ${u.valueInGram ?? ''} - ${u.type || ''}`.trim()
              : '';
          const displayLabel = String(
            u.displayLabel ||
              u.DisplayLabel ||
              u.name ||
              u.Name ||
              u.unitName ||
              u.UnitName ||
              composed ||
              u.label ||
              u.Label ||
              ''
          ).trim();
          return {
            servingUnitMasterId,
            displayLabel: displayLabel || String(servingUnitMasterId || ''),
            valueInGram: Number(u.valueInGram ?? u.ValueInGram ?? 0) || undefined,
          };
        })
        .filter((u) => u.servingUnitMasterId != null && u.servingUnitMasterId !== '');
    } catch {
      this.ingredientServingUnits = [];
    }
    if (!this.ingredientServingUnits.length) {
      this.seedDefaultIngredientUnits();
    }
    this.cdRef.detectChanges();
  }

  saveIngredient(): void {
    // If user typed a unit but didn't press Enter/select, commit that typed value.
    const typedUnit = String(this.pendingUnitSearchTerm || '').trim();
    if (
      typedUnit &&
      (this.selectedServingUnitId == null || String(this.selectedServingUnitId).trim() === '')
    ) {
      const created = this.addCustomIngredientUnit(typedUnit);
      if (created) {
        const exists = this.ingredientServingUnits.some(
          (u) =>
            String(u.servingUnitMasterId) === String(created.servingUnitMasterId) ||
            String(u.displayLabel || '').trim().toLowerCase() ===
              created.displayLabel.toLowerCase()
        );
        if (!exists) {
          this.ingredientServingUnits = [created, ...this.ingredientServingUnits];
        }
        this.selectedServingUnitId = created.servingUnitMasterId;
      }
    }

    const unit = this.ingredientServingUnits.find(
      (u) => String(u.servingUnitMasterId) === String(this.selectedServingUnitId)
    );
    const hasMasterId =
      this.selectedIngredientId != null && Number(this.selectedIngredientId) > 0;
    const unitIdRaw = this.selectedServingUnitId != null ? String(this.selectedServingUnitId) : '';
    let unitLabel = String(unit?.displayLabel || '').trim();
    if (!unitLabel || unitLabel === '—') {
      unitLabel = typedUnit;
    }

    // Resolve free-text / alias units onto master lookup ids when possible.
    let servingUnitMasterId = /^\d+$/.test(unitIdRaw) ? unitIdRaw : '';
    if (!servingUnitMasterId && unitLabel) {
      const resolved = (this.lookups?.units || []).find((u) => {
        const name = String(u.name || '').trim().toLowerCase();
        const want = unitLabel.toLowerCase();
        return (
          name === want ||
          name === want.replace(/%/g, 'percent') ||
          (want === 'g' && (name === 'gm' || name === 'gram' || name === 'grams')) ||
          (want === '%' && name.includes('percent'))
        );
      });
      if (resolved) {
        servingUnitMasterId = String(resolved.id);
        unitLabel = resolved.name || unitLabel;
      }
    }

    const row: FpBulkResolvedIngredient = {
      ingredientId: hasMasterId ? String(this.selectedIngredientId) : '',
      ingredientRefId: this.pendingIngredientTaxonomy?.ingredientRefId
        ? String(this.pendingIngredientTaxonomy.ingredientRefId)
        : undefined,
      name: this.selectedIngredientName || 'Ingredient',
      servingUnitMasterId,
      unit: unitLabel && unitLabel !== '—' ? unitLabel : '',
      amount: this.ingredientAmount != null ? String(this.ingredientAmount) : '',
      isNew: !hasMasterId,
    };
    if (this.pendingIngredientTaxonomy) {
      applyTaxonomyToIngredient(row, this.pendingIngredientTaxonomy);
    } else if (this.ingredientEditIndex != null) {
      // Keep existing taxonomy when editing amount/unit without re-picking.
      const prev = this.selections.ingredients[this.ingredientEditIndex];
      if (prev) {
        row.ingredientRefId = prev.ingredientRefId || row.ingredientRefId;
        row.faoFoodGroupName = prev.faoFoodGroupName;
        row.allergenNames = prev.allergenNames;
        row.allergenIds = prev.allergenIds ? [...prev.allergenIds] : undefined;
        row.intoleranceNames = prev.intoleranceNames;
        row.intoleranceIds = prev.intoleranceIds ? [...prev.intoleranceIds] : undefined;
        row.allergenFreeIds = prev.allergenFreeIds ? [...prev.allergenFreeIds] : undefined;
        row.intoleranceFreeIds = prev.intoleranceFreeIds
          ? [...prev.intoleranceFreeIds]
          : undefined;
        row.category = prev.category;
        row.insNumber = prev.insNumber;
        row.taxonomyHydrated = prev.taxonomyHydrated;
      }
    }
    if (this.ingredientEditIndex != null) {
      this.selections.ingredients[this.ingredientEditIndex] = row;
    } else {
      this.selections.ingredients.push(row);
    }
    this.pendingUnitSearchTerm = '';
    this.pendingIngredientTaxonomy = null;
    this.syncAllergensFromIngredients();
    this.ingredientModalRef?.close();
    this.ingredientModalRef = null;
    this.ingredientEditIndex = null;
  }

  removeIngredient(index: number): void {
    this.selections.ingredients.splice(index, 1);
    this.syncAllergensFromIngredients();
  }

  getIngredientFaoLabel(ing: FpBulkResolvedIngredient): string {
    return String(ing?.faoFoodGroupName || '').trim() || '—';
  }

  getIngredientAllergenLabel(ing: FpBulkResolvedIngredient): string {
    const named = String(ing?.allergenNames || '').trim();
    if (named) return named;
    const ids = ing?.allergenIds || [];
    if (!ids.length) return '—';
    const names = ids
      .map((id) => (this.lookups.allergens || []).find((a) => String(a.id) === String(id))?.name)
      .filter(Boolean);
    return names.length ? names.join(', ') : '—';
  }

  private syncAllergensFromIngredients(): void {
    syncFpBulkAllergensFromIngredients(this.selections, this.lookups);
    this.cdRef.detectChanges();
  }

  private async fetchIngredientTaxonomy(
    ingredientId: number
  ): Promise<FpBulkIngredientTaxonomy | null> {
    try {
      const res: any = await this.masterService.getIngredientById(String(ingredientId));
      const detail = res?.data ?? res?.Data ?? res?.response ?? res?.Response ?? res;
      return extractFpIngredientTaxonomyFromDetail(detail);
    } catch {
      return null;
    }
  }

  private async ensureIngredientsEnriched(): Promise<void> {
    rebuildFpBulkPayload(this.row, this.lookups);
    const list = this.selections.ingredients || [];
    const needsWork = list.some(
      (i) =>
        !!String(i.name || '').trim() &&
        (!/^\d+$/.test(String(i.ingredientRefId || '').trim()) ||
          !i.taxonomyHydrated ||
          !String(i.ingredientId || '').trim()) &&
        (!i.faoFoodGroupName ||
          (!(i.allergenIds || []).length && !i.allergenNames) ||
          (!(i.intoleranceIds || []).length && !i.intoleranceNames) ||
          !String(i.ingredientId || '').trim() ||
          !/^\d+$/.test(String(i.ingredientRefId || '').trim()))
    );
    if (!list.length || !needsWork) {
      this.syncAllergensFromIngredients();
      return;
    }
    try {
      await enrichFpBulkRowsFromIngredients([this.row], {
        bulkResolveIngredients: async (names) => this.resolveIngredientNamesBulk(names),
        getIngredientDetail: async (ingredientId) => this.fetchIngredientTaxonomy(ingredientId),
      });
      rebuildFpBulkPayload(this.row, this.lookups);
    } catch (err) {
      console.error('Failed to enrich finish-product row ingredients', err);
      this.syncAllergensFromIngredients();
    }
    this.cdRef.detectChanges();
  }

  private async resolveIngredientNamesBulk(
    names: string[]
  ): Promise<Map<string, { id: number; name: string; ingredientRefId?: number | null } | null>> {
    const map = new Map<string, { id: number; name: string; ingredientRefId?: number | null } | null>();
    const unique = Array.from(
      new Set((names || []).map((n) => String(n || '').trim()).filter(Boolean))
    );
    if (!unique.length) return map;

    const res: any = await this.masterService.resolveIngredientsByNames(unique);
    const payload = res?.data ?? res?.response ?? res?.Response ?? res;
    const matches = Array.isArray(payload?.matches)
      ? payload.matches
      : Array.isArray(payload?.Matches)
        ? payload.Matches
        : [];

    for (const m of matches) {
      const query = String(m?.queryName ?? m?.QueryName ?? '').trim();
      if (!query) continue;
      const key = query.toLowerCase().replace(/\s+/g, ' ');
      const matched = !!(m?.matched ?? m?.Matched);
      if (!matched) {
        map.set(key, null);
        continue;
      }
      const id = Number(
        m?.id ?? m?.Id ?? m?.ingredientMasterId ?? m?.IngredientMasterId ?? 0
      );
      const refId = Number(m?.ingredientRefId ?? m?.IngredientRefId ?? 0);
      map.set(key, {
        id: Number.isFinite(id) && id > 0 ? id : 0,
        ingredientRefId: Number.isFinite(refId) && refId > 0 ? refId : null,
        name:
          m?.ingredientName ||
          m?.IngredientName ||
          m?.commonName ||
          m?.CommonName ||
          query,
      });
    }
    return map;
  }

  save(): void {
    const errors = getFpBulkFieldErrors(this.parsed, this.selections);
    if (errors.length) {
      this.applyAllValidationErrors();
      this.activeTab = errors[0].section;
      this.refreshSectionErrorBanner();
      this.scheduleLucideIconRender();
      try {
        const body = document.querySelector('.saru-editor__body');
        body?.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        /* noop */
      }
      return;
    }
    this.fieldErrors = {};
    this.sectionErrorMessages = [];
    rebuildFpBulkPayload(this.row, this.lookups);
    this.activeModal.close(this.row);
  }

  async onEntityLicenseSelected(
    event: Event,
    key:
      | 'manufacturedBy'
      | 'marketedBy'
      | 'packagedBy'
      | 'importedBy'
      | 'exportedBy'
      | 'packagedBySection4'
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      input.value = '';
      return;
    }

    this.entityLicenseUploading[key] = true;
    this.cdRef.markForCheck();
    try {
      const res: any = await this.finishProductService.uploadProductImages([file]);
      const payload = res?.response ?? res?.Response ?? res?.data ?? res;
      const urls =
        payload?.urls ||
        payload?.imageUrls ||
        payload?.files ||
        (Array.isArray(payload) ? payload : null) ||
        [];
      const url = Array.isArray(urls) ? urls[0] : typeof urls === 'string' ? urls : null;
      if (url) {
        const entity = (this.selections as any)[key] as FpBulkEntitySelections;
        if (entity) entity.licenseImage = String(url);
      }
    } catch (err) {
      console.error('Entity license upload failed', err);
    } finally {
      this.entityLicenseUploading[key] = false;
      input.value = '';
      this.cdRef.detectChanges();
      this.scheduleLucideIconRender();
    }
  }

  clearEntityLicense(
    key:
      | 'manufacturedBy'
      | 'marketedBy'
      | 'packagedBy'
      | 'importedBy'
      | 'exportedBy'
      | 'packagedBySection4'
  ): void {
    const entity = (this.selections as any)[key] as FpBulkEntitySelections;
    if (entity) entity.licenseImage = null;
    this.cdRef.detectChanges();
  }

  getEntityLicensePreviewUrl(
    key:
      | 'manufacturedBy'
      | 'marketedBy'
      | 'packagedBy'
      | 'importedBy'
      | 'exportedBy'
      | 'packagedBySection4'
  ): string | null {
    const entity = (this.selections as any)[key] as FpBulkEntitySelections;
    return this.resolveEntityMediaUrl(entity?.licenseImage);
  }

  private resolveEntityMediaUrl(rawValue?: string | null): string | null {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw) || /^blob:/i.test(raw) || /^data:/i.test(raw)) return raw;
    if (raw.startsWith('//')) return `https:${raw}`;
    if (raw.startsWith('assets/')) return raw;
    const base = (environment.baseUrl || '').replace(/\/$/, '');
    let path = raw.replace(/^\//, '');
    if (!path.includes('/') && !path.startsWith('uploads/')) {
      path = `uploads/products/${path}`;
    }
    const encodedPath = path
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    return base ? `${base}/${encodedPath}` : `/${encodedPath}`;
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  formatIngredientUnit(raw: string | null | undefined): string {
    const label = String(raw || '').trim();
    if (!label) return '—';
    // "Standard - cup - 149.00 - Gram" -> "cup"
    const parts = label.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const mid = parts[1];
      if (mid && !/^\d+(\.\d+)?$/.test(mid)) return mid;
    }
    return label.length > 28 ? label.slice(0, 26) + '…' : label;
  }

  private scheduleLucideIconRender(): void {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if ((window as any).lucide?.createIcons) {
        (window as any).lucide.createIcons();
      }
    }, 50);
  }
}
